/**
 * Session Store - 用户登录态管理
 * @description 将 useSession Hook 中的 userInfo 相关逻辑迁移到 Zustand Store
 * @author AI Assistant
 * @date 2025-01-07
 */

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { derive } from 'derive-zustand'
import { message } from 'antd'
import { stringify } from 'qs'
import { remove, get as getCookie } from 'js-cookie'
import { uniqBy } from 'lodash'
import { StaffisAdmin, StaffisAssign, StaffStatus, UserInfo, CompanyInfo, ModuleInfo } from '@types'
import {
  clearTccToken,
  getUserInfo as getUserInfoApi,
  login,
  getModuleCompanyMapNoSession
} from '@services/session'
import { ORIGIN_TCC_TOKEN, moduleId } from '@configs'

// ========== 常量定义 ==========
// 登录方式枚举
const LOGIN_TYPES = {
  WECHAT_QUICK: 'wechat_quick', // 企微快速登录（模块 7、8、13）
  CUSTOM_SSO: 'custom_sso', // 自定义 SSO 登录（模块 12）
  LEGACY: 'legacy' // 传统登录方式（其他模块）
} as const

// 根据模块 ID 获取登录方式
const getLoginType = (currentModuleId: number) => {
  // 模块 7、8、13：企微快速登录
  if ([moduleId.MID_LONG_TAIL_ID, moduleId.MID_LONG_TAIL_CLEAN_ID, moduleId.SASS_ID].includes(currentModuleId)) {
    return LOGIN_TYPES.WECHAT_QUICK
  }

  // 模块 12：自定义 SSO 登录
  // if (currentModuleId === moduleId.COC_TURN_ID) {
  //   return LOGIN_TYPES.CUSTOM_SSO
  // }

  // 其他模块：传统登录方式
  return LOGIN_TYPES.LEGACY
}

// ========== 接口定义 ==========
interface SessionState {
  // 状态
  logging: boolean
  userInfo: UserInfo
  companyList: CompanyInfo[]
  moduleList: ModuleInfo[]

  // 异步方法
  getUserInfo: () => Promise<UserInfo>
  loginWithCode: (
    code: string,
    moduleId: number,
    appId: string,
    agentId: number,
    tccCompany: number
  ) => Promise<UserInfo>
  logout: (push: (path: string) => void) => Promise<void>
  jumpToWorkWechat: (appId: string, agentId: string, callbackUrl?: string) => void
  getModuleCompanyMap: () => Promise<void>
  forceRequestModuleCompanyMap: () => Promise<void>

  // 同步方法 - 兼容 useObject 的所有调用方式
  setUserInfo: {
    (key: keyof UserInfo, value: any): void
    (data: Partial<UserInfo>): void
    (fn: (draft: UserInfo) => void): void
  }

  // 计算属性
  logged: () => boolean

  // 业务方法
  isPermissableBelongModule: (moduleIds: number[]) => boolean

  // 重置
  reset: () => void
}

// ========== 初始状态 ==========
const initialUserInfo: UserInfo = {
  salesName: '',
  salesNameCh: '',
  avatar: '',
  isAdmin: StaffisAdmin.NOTADMIN,
  isAssign: StaffisAssign.NO,
  belongModule: -1,
  companyId: -1,
  createTime: '',
  id: -1,
  lastModifier: '',
  leader: '',
  openid: '-1',
  poolNum: -1,
  qidianWorkNumber: '-1',
  status: StaffStatus.OFFDUTY,
  updateTime: '',
  salesPhone: '',
  weworkShortChain: '',
  useT3C: false,
  useMeeting: false,
  useNewT3CCallBar: false,
}

// ========== Store 创建 ==========
export const sessionStore = create<SessionState>()(
  immer((set, get) => ({
    // ========== 初始状态 ==========
    logging: true,
    userInfo: initialUserInfo,
    companyList: [],
    moduleList: [],

    // ========== 异步方法 ==========
    getUserInfo: async () => {
      const [res, err] = await getUserInfoApi()

      set(draft => {
        draft.logging = false
        if (!err) {
          draft.userInfo = res
        }
      })

      if (err) {
        throw err
      }

      return res
    },

    getModuleCompanyMap: async () => {
      const [res, err] = await getModuleCompanyMapNoSession()

      if (!err && res) {
        set(draft => {
          // 设置公司列表
          draft.companyList = Array.from(res.list || [])

          // 生成模块列表（去重）
          draft.moduleList = uniqBy(
            draft.companyList.map(company => {
              const { belongModule, belongModuleName } = company
              return { belongModule, belongModuleName }
            }),
            'belongModule'
          )
        })
      }

      if (err) {
        throw err
      }
    },

    forceRequestModuleCompanyMap: async () => {
      // 强制刷新模块公司映射
      await get().getModuleCompanyMap()
    },

    loginWithCode: async (code, moduleId, appId, agentId, tccCompany) => {
      remove(ORIGIN_TCC_TOKEN)

      const [, err] = await login(code, moduleId, appId, agentId, tccCompany)

      if (err) {
        throw err
      }

      return await get().getUserInfo()
    },

    logout: async (push) => {
      // 静默失败
      await clearTccToken()

      message.success('退出登录成功')

      set(draft => {
        draft.logging = true
        draft.userInfo = initialUserInfo
      })

      push(`/login?${stringify({
        callbackUrl: window.location.pathname + window.location.search
      })}`)
    },

    jumpToWorkWechat: (appId, agentId, callbackUrl) => {
      const { origin } = window.location

      // 从 cookie 中获取当前选择的模块
      const currentModuleId = parseInt(getCookie('tcc-module') || '0')

      const loginType = getLoginType(currentModuleId)

      switch (loginType) {
        case LOGIN_TYPES.WECHAT_QUICK: {
          // 模块 7、8、13：企微快速登录，添加 login_type 参数
          const query = stringify({
            login_type: 'CorpApp',
            appid: appId,
            agentid: agentId,
            redirect_uri: `${origin}/login`,
            state: stringify({ callbackUrl, currentModuleId }),
          })
          window.location.href = `https://login.work.weixin.qq.com/wwlogin/sso/login?${query}`
          break
        }

        // case LOGIN_TYPES.CUSTOM_SSO: {
        //   // 模块 12：自定义 SSO 登录（预留，待实现）
        //   // TODO: 实现模块 12 的自定义 SSO 登录逻辑
        //   console.warn('模块 12 的自定义 SSO 登录逻辑待实现')

        //   // 临时使用传统登录方式
        //   const query = stringify({
        //     appid: appId,
        //     agentid: agentId,
        //     redirect_uri: `${origin}/login`,
        //     state: stringify({ callbackUrl, currentModuleId }),
        //   })
        //   window.location.href = `https://open.work.weixin.qq.com/wwopen/sso/qrConnect?${query}`
        //   break
        // }

        case LOGIN_TYPES.LEGACY:
        default: {
          // 其他模块：传统登录方式
          const query = stringify({
            appid: appId,
            agentid: agentId,
            redirect_uri: `${origin}/login`,
            state: encodeURIComponent(stringify({ callbackUrl, currentModuleId })),
          })

          window.location.href = `https://open.work.weixin.qq.com/wwopen/sso/qrConnect?${query}`
          break
        }
      }
    },

    // ========== 同步方法 - 完全兼容 useObject API ==========
    /**
     * 设置用户信息
     * 支持 3 种调用方式：
     * 1. setUserInfo('salesName', 'newName') - 设置单个属性
     * 2. setUserInfo({ salesName: 'newName' }) - 浅合并对象
     * 3. setUserInfo(draft => { draft.salesName = 'newName' }) - 函数更新
     */
    setUserInfo: (data: any, value?: any) => {
      set(draft => {
        if (typeof data === 'string') {
          // 单个属性设置
          ;(draft.userInfo as any)[data] = value
        } else if (typeof data === 'function') {
          // 函数方式
          data(draft.userInfo)
        } else if (typeof data === 'object' && data !== null) {
          // 对象方式 - 浅合并
          Object.assign(draft.userInfo, data)
        }
      })
    },

    // ========== 计算属性 ==========
    logged: () => !get().logging,

    // ========== 业务方法 ==========
    /**
     * 判断当前用户所属模块是否在指定模块列表中
     * @param moduleIds 模块ID数组
     * @returns 是否匹配
     */
    isPermissableBelongModule: (moduleIds: number[]) => {
      const { userInfo } = get()
      return moduleIds.some(moduleId => moduleId === userInfo.belongModule)
    },

    // ========== 重置方法 ==========
    reset: () => {
      set({
        logging: true,
        userInfo: initialUserInfo,
        companyList: [],
        moduleList: [],
      })
    },
  }))
)

// ========== 响应式计算属性（derive-zustand）==========

/**
 * 当前模块下的公司列表（响应式）
 * 当 userInfo.belongModule 或 companyList 变化时自动更新
 */
export const currCompanyList = derive<CompanyInfo[]>((get) => {
  const { companyList, userInfo } = get(sessionStore)
  return companyList.filter(company => company.belongModule === userInfo.belongModule)
})

/**
 * 是否应该初始化 T3C SDK（响应式）
 * 条件：
 * 1. useT3C 开关打开
 * 2. 用户已绑定邮箱（email 非空）
 */
export const shouldInitT3C = derive<boolean>((get) => {
  const { userInfo } = get(sessionStore)
  const hasEmail = userInfo.email && userInfo.email.trim() !== ''
  return userInfo.useT3C && !!hasEmail
})

export type { SessionState }
